import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { useAuth } from "react-oidc-context";
import config from "./config.json";
import AppContext from "./AppContext";

import Radio from "@mui/material/Radio";
import Stack from "@mui/material/Stack";
import Slider from "@mui/material/Slider";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Divider from '@mui/material/Divider';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import RadioGroup from "@mui/material/RadioGroup";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Autocomplete from '@mui/material/Autocomplete';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from "@mui/material/Tooltip";
import InfoIcon from "@mui/icons-material/Info";
import { Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Alert } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Drawer, useTheme, useMediaQuery, Theme, Box } from "@mui/material";
import MenuIcon from '@mui/icons-material/Menu';
import TextField from "@mui/material/TextField";
import TuneIcon from '@mui/icons-material/Tune';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import SchemaOutlinedIcon from '@mui/icons-material/SchemaOutlined';
import AutoStoriesOutlinedIcon from '@mui/icons-material/AutoStoriesOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ChatPrompt from "./ChatPrompt";
import GlueupEventsExport from "./GlueupEventsExport";

// --- Data Structures for Subjects ---
interface SubjectConfig {
    name: string;
    contextLabel: string;
    contextPlaceholder: string;
    contexts: string[];
    questions: { display: string; template: string }[];
}

// "glueup" is a special subject: no document picker, no standard questions,
// no embedding/RAG. The backend (handle_complete) routes subject=="glueup"
// to a stuff-the-context path that reads all events from glueup.events_t.
// We inject this entry client-side so it shows in the subject dropdown
// without requiring a DynamoDB metadata row.
const GLUEUP_SUBJECT_KEY = "glueup";
const GLUEUP_SUBJECT_CONFIG: SubjectConfig = {
    name: "Climate Week Zurich",
    contextLabel: "",
    contextPlaceholder: "",
    contexts: [],
    questions: [],
};

const SUBJECT_DESCRIPTIONS: Record<string, string> = {
    C40: "Climate action plans, emission targets and urban sustainability across C40 member cities.",
    IPCC: "Assessment reports and scenarios from the IPCC Working Groups.",
    IEA: "Energy outlooks, policy trackers and technology roadmaps from the IEA.",
    glueup: "Events at Climate Week Zurich 2026 — dates, venues, speakers, topics and organisers.",
};

// --- API Function ---
const getMetadata = async (auth: any) => {
    const userApiKey = auth.user?.profile?.['custom:api_key'] as string;
    if (!auth.user?.id_token || !userApiKey) {
        throw new Error("Authentication error: API key or token is missing.");
    }
    const baseChatbotUrl = config.api.chatbot.url.endsWith('/') ? config.api.chatbot.url.slice(0, -1) : config.api.chatbot.url;
    const metadataUrl = `${baseChatbotUrl}/metadata`;

    const response = await axios.get(metadataUrl, {
        headers: {
            'Authorization': auth.user.id_token,
            'X-Api-Key': userApiKey
        },
        timeout: config.timeouts.chatApi
    });
    return response.data;
};

function ChatPage() {
    const defaultScoreThreshold = 0.1;
    const defaultHistoryRetention = 1;
    const defaultListTextChunks = false;

    // --- STATE MANAGEMENT ---
    const auth = useAuth();
    const appContext = useContext(AppContext);

    // Data fetching state.
    // Seed subjectData with glueup so the UI is interactive immediately on
    // mount — glueup is injected client-side (no DynamoDB row), so there's
    // no reason to wait for the /metadata roundtrip before showing it.
    // The other subjects get merged in when metadata arrives.
    const [subjectData, setSubjectData] = useState<Record<string, SubjectConfig>>({
        [GLUEUP_SUBJECT_KEY]: GLUEUP_SUBJECT_CONFIG,
    });
    const [isLoadingData, setIsLoadingData] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Component state
    const [mobileOpen, setMobileOpen] = useState(false);
    const [scoreThreshold, setScoreThreshold] = useState(defaultScoreThreshold);
    const [historyRetention, setHistoryRetention] = useState(defaultHistoryRetention);
    const [listTextChunksFlag, setListTextChunksFlag] = useState(defaultListTextChunks);
    const [question, setQuestion] = useState("");
    const [openFirstDialog, setOpenFirstDialog] = useState(false);
    const [openSecondDialog, setOpenSecondDialog] = useState(false);
    const [openPdfDialog, setOpenPdfDialog] = useState(false);
    const [exportEventsOpen, setExportEventsOpen] = useState(false);
    const [autoSendTrigger, setAutoSendTrigger] = useState(0);
    // currentSubject lives in AppContext so it survives navigation away
    // and back. Falls back to "C40" if context not yet provided (shouldn't
    // happen at runtime, just satisfies TS).
    const currentSubject = appContext?.currentSubject ?? "glueup";
    const setCurrentSubject = appContext?.setCurrentSubject ?? (() => {});
    const [advancedSettingsOpen, setAdvancedSettingsOpen] = useState(false);
    // Seed with glueup's default so questions work without waiting on /metadata.
    const [selectedContexts, setSelectedContexts] = useState<Record<string, string>>({
        [GLUEUP_SUBJECT_KEY]: "All",
    });

    // --- EFFECT for fetching metadata ---
    useEffect(() => {
        const fetchMetadata = async () => {
            if (!auth.isAuthenticated) {
                return; // Don't fetch if not authenticated
            }
            try {
                setIsLoadingData(true);
                const data: Record<string, SubjectConfig> = await getMetadata(auth);
                // Merge fetched subjects on top of whatever is already in state
                // (glueup is pre-seeded; user may have already interacted).
                data[GLUEUP_SUBJECT_KEY] = GLUEUP_SUBJECT_CONFIG;
                setSubjectData((prev: Record<string, SubjectConfig>) => ({ ...prev, ...data }));

                const initialContexts: Record<string, string> = {};
                Object.keys(data).forEach(key => {
                    if (key === "C40" || key === "IPCC") {
                        initialContexts[key] = ""; // C40/IPCC start with no context selected
                    } else {
                        // glueup + other subjects: default to "All" so the
                        // selectedDocument payload field is non-empty (the
                        // backend ignores it for glueup anyway).
                        initialContexts[key] = "All";
                    }
                });
                // Keep any selection the user already made (prev wins over defaults).
                setSelectedContexts((prev: Record<string, string>) => ({ ...initialContexts, ...prev }));

                setError(null);
            } catch (err) {
                console.error("Failed to fetch metadata:", err);
                setError("Failed to load subject data. Please try again later.");
            } finally {
                setIsLoadingData(false);
            }
        };

        fetchMetadata();
    }, [auth.isAuthenticated, auth]);

    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const desktopFontSize = "0.8rem";
    const mobileFontSize = "0.7rem";
    const labelFontSize = isSmallScreen ? mobileFontSize : desktopFontSize;
    const titleFontSize = isSmallScreen ? "0.85rem" : "1.0rem";

    const appBarHeight = (theme.mixins.toolbar as { minHeight?: number }).minHeight ?? 56;
    const drawerWidth = isSmallScreen ? "80vw" : "30em";

    // --- Handlers ---
    const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
    const handleScoreThresholdChange = (event: Event, newValue: number | number[]) => setScoreThreshold(newValue as number);
    const handleHistoryRetentionChange = (event: Event, newValue: number | number[]) => setHistoryRetention(newValue as number);
    const handleListTextChunksFlagChange = (event: React.ChangeEvent<HTMLInputElement>) => setListTextChunksFlag(event.target.value === "1");
    
    const handleOpenAdvancedSettings = () => setAdvancedSettingsOpen(true);
    const handleCloseAdvancedSettings = () => setAdvancedSettingsOpen(false);

    const handleSubjectMenuItemClick = (newSubject: string) => {
        if (newSubject !== currentSubject) {
            // Different subjects have different scopes (different document set,
            // different system prompt, different backend code path). Carrying
            // the previous subject's chat history into a new one is confusing
            // and gives the model misleading context — wipe it.
            appContext?.setChatMessages([]);
            appContext?.setChatHistory([]);
            appContext?.setSubmittedFeedback([]);
        }
        setCurrentSubject(newSubject);
        const newDefaultContext = (newSubject === "C40" || newSubject === "IPCC") ? "" : "All";
        setSelectedContexts(prev => ({ ...prev, [newSubject]: newDefaultContext }));
    };

    const handleContextChange = (event: React.SyntheticEvent, newValue: string | null) => {
        setSelectedContexts(prev => ({ ...prev, [currentSubject]: newValue || "" }));
    };

    const handleQuestionClick = (questionTemplate: string) => {
        const currentContextValue = selectedContexts[currentSubject];
        const currentConfig = subjectData[currentSubject];
        if (!currentConfig || !currentContextValue) return;
        
        const contextForQuestion = currentContextValue === "All" 
            ? `all of ${currentConfig.contextPlaceholder.toLowerCase()}s`
            : currentContextValue;

        const fullQuestion = questionTemplate.replace(/\[context\]/gi, contextForQuestion);

        setQuestion(fullQuestion);
        setAutoSendTrigger(prev => prev + 1);
        if(isSmallScreen) handleDrawerToggle();
    };

    const resetLabSettings = () => { 
        setScoreThreshold(defaultScoreThreshold);
        setHistoryRetention(defaultHistoryRetention); 
        setListTextChunksFlag(defaultListTextChunks); 
    };
    const handleOpenFirstDialog = () => setOpenFirstDialog(true);
    const handleCloseFirstDialog = () => setOpenFirstDialog(false);
    const handleOpenSecondDialog = () => setOpenSecondDialog(true);
    const handleCloseSecondDialog = () => setOpenSecondDialog(false);
    const handleClosePdfDialog = () => setOpenPdfDialog(false);
    // --- End Handlers ---

    const isAnyDialogOpen = advancedSettingsOpen || openFirstDialog || openSecondDialog || openPdfDialog || exportEventsOpen;

    const tooltipText = (text: string) => ( <Typography component="span" sx={{ fontSize: labelFontSize, display: "block", whiteSpace: "pre-line", maxWidth: isSmallScreen ? '200px' : 'none' }} > {text} </Typography> );

    const sidePanel = (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'background.paper', borderRight: { sm: '1px solid' }, borderColor: { sm: 'divider' } }}>
            {/* Header: subject selector + description */}
            <Box sx={{ px: 2, pt: 2, pb: 1.5, flexShrink: 0 }}>
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, letterSpacing: '0.08em', color: 'text.disabled', textTransform: 'uppercase', mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    Knowledge source · pick one
                    {isLoadingData && <CircularProgress size={10} thickness={6} />}
                </Typography>
                <FormControl fullWidth size="small">
                    <Select
                        id="subject-selector"
                        value={subjectData[currentSubject] ? currentSubject : ''}
                        onChange={(e: SelectChangeEvent) => handleSubjectMenuItemClick(e.target.value)}
                        displayEmpty
                        startAdornment={<AutoStoriesOutlinedIcon sx={{ fontSize: '1.05rem', color: 'primary.main', mr: 1 }} />}
                        sx={{
                            fontSize: titleFontSize,
                            fontWeight: 600,
                            borderRadius: 2,
                            bgcolor: 'background.paper',
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                        }}
                        renderValue={(selected: unknown) => {
                            const key = selected as string;
                            if (!key) return <Box component="span" sx={{ color: 'text.disabled' }}>Select subject</Box>;
                            return subjectData[key]?.name || key;
                        }}
                    >
                        {Object.keys(subjectData).map(subjectKey => (
                            <MenuItem
                                key={subjectKey}
                                value={subjectKey}
                                disabled={subjectKey === "All"}
                                sx={{ fontSize: labelFontSize }}
                            >
                                {subjectData[subjectKey].name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {SUBJECT_DESCRIPTIONS[currentSubject] && (
                    <Typography sx={{ mt: 1, fontSize: labelFontSize, color: 'text.secondary', lineHeight: 1.5 }}>
                        {SUBJECT_DESCRIPTIONS[currentSubject]}
                    </Typography>
                )}
                {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
            </Box>

            <Divider sx={{ mx: 2 }} />

            {/* Scrollable middle: context + suggestions */}
            <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 2, pt: 1.5, pb: 1.5, minHeight: 0 }}>
                {!error && (() => {
                    const currentConfig = subjectData[currentSubject];
                    if (!currentConfig) return null;

                    // glueup uses stuff-the-context: no document picker,
                    // no standard-question buttons. Instead, surface the
                    // event-export action prominently — it's the only
                    // sidebar affordance users need for this subject.
                    if (currentSubject === GLUEUP_SUBJECT_KEY) {
                        return (
                            <>
                                <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, letterSpacing: '0.08em', color: 'text.disabled', textTransform: 'uppercase', mb: 0.75 }}>
                                    Export
                                </Typography>
                                <Button
                                    variant="contained"
                                    fullWidth
                                    size="medium"
                                    startIcon={<FileDownloadOutlinedIcon />}
                                    onClick={() => setExportEventsOpen(true)}
                                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                                >
                                    Export events to CSV
                                </Button>
                                <Typography sx={{ mt: 1, fontSize: labelFontSize, color: 'text.secondary', lineHeight: 1.5 }}>
                                    Download the full event list with all columns.
                                </Typography>
                            </>
                        );
                    }

                    const showAllOption = currentSubject !== "C40" && currentSubject !== "IPCC";
                    const autocompleteOptions = showAllOption
                        ? ["All", ...currentConfig.contexts]
                        : currentConfig.contexts;
                    const contextSelected = Boolean(selectedContexts[currentSubject]);
                    const showQuestions = (currentSubject === "C40" || currentSubject === "IPCC") && currentConfig.questions.length > 0;
                    const placeholderName = currentConfig.contextPlaceholder || currentConfig.contextLabel || 'option';
                    const filterLabelText = `Filter by ${placeholderName.toLowerCase()}`;

                    return (
                        <>
                            <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, letterSpacing: '0.08em', color: 'text.disabled', textTransform: 'uppercase', mb: 0.75 }}>
                                {filterLabelText}
                            </Typography>
                            <Autocomplete
                                id={`${currentSubject}-context-autocomplete`}
                                options={autocompleteOptions}
                                value={selectedContexts[currentSubject] || null}
                                onChange={handleContextChange}
                                fullWidth
                                size="small"
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        placeholder={`Select a ${placeholderName.toLowerCase()}…`}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <FilterAltOutlinedIcon sx={{ fontSize: '1rem', color: selectedContexts[currentSubject] ? 'primary.main' : 'text.disabled', ml: 0.25, mr: 0.25 }} />
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                            sx: { fontSize: labelFontSize, borderRadius: 2 },
                                        }}
                                    />
                                )}
                                ListboxProps={{ style: { fontSize: labelFontSize } }}
                            />

                            {showQuestions && (
                                <Box sx={{ mt: 2.5 }}>
                                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, letterSpacing: '0.08em', color: 'text.disabled', textTransform: 'uppercase', mb: 0.75 }}>
                                        Suggested questions
                                    </Typography>
                                    {!contextSelected && (
                                        <Typography sx={{ fontSize: labelFontSize, color: 'text.disabled', mb: 1 }}>
                                            Pick a {placeholderName.toLowerCase()} above to enable.
                                        </Typography>
                                    )}
                                    <Stack spacing={0.75}>
                                        {currentConfig.questions.map((questionItem, index) => {
                                            const display = questionItem.display.replace(
                                                /\[Context\]/i,
                                                selectedContexts[currentSubject] || `[${currentConfig.contextPlaceholder}]`
                                            );
                                            return (
                                                <ButtonBase
                                                    key={index}
                                                    onClick={() => handleQuestionClick(questionItem.template)}
                                                    disabled={!contextSelected}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        textAlign: 'left',
                                                        width: '100%',
                                                        px: 1.25,
                                                        py: 1.1,
                                                        borderRadius: 2,
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        bgcolor: 'background.paper',
                                                        color: 'text.primary',
                                                        fontSize: labelFontSize,
                                                        lineHeight: 1.4,
                                                        transition: 'background-color 120ms, border-color 120ms, transform 120ms',
                                                        '&:hover:not(.Mui-disabled)': {
                                                            bgcolor: 'action.hover',
                                                            borderColor: 'primary.main',
                                                            transform: 'translateX(2px)',
                                                        },
                                                        '&.Mui-disabled': {
                                                            opacity: 0.5,
                                                        },
                                                    }}
                                                >
                                                    <AutoAwesomeOutlinedIcon sx={{ fontSize: '0.95rem', color: 'primary.main', flexShrink: 0 }} />
                                                    <Box component="span" sx={{ flexGrow: 1 }}>{display}</Box>
                                                </ButtonBase>
                                            );
                                        })}
                                    </Stack>
                                </Box>
                            )}
                        </>
                    );
                })()}
            </Box>

            {/* Footer: icon-button row */}
            <Box sx={{ flexShrink: 0, borderTop: '1px solid', borderColor: 'divider', px: 1.5, py: 1 }}>
                <Stack direction="row" spacing={0.5} justifyContent="space-around">
                    <Tooltip title="Advanced settings">
                        <IconButton size="small" onClick={handleOpenAdvancedSettings} sx={{ color: 'text.secondary' }}>
                            <TuneIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Diagram">
                        <IconButton size="small" onClick={handleOpenFirstDialog} sx={{ color: 'text.secondary' }}>
                            <SchemaOutlinedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Architecture">
                        <IconButton size="small" onClick={handleOpenSecondDialog} sx={{ color: 'text.secondary' }}>
                            <AccountTreeIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Box>
            <GlueupEventsExport open={exportEventsOpen} onClose={() => setExportEventsOpen(false)} />

                {/* --- DIALOGS --- */}
                <Dialog open={advancedSettingsOpen} onClose={handleCloseAdvancedSettings} maxWidth="xs" fullWidth>
                    <DialogTitle>
                        Advanced Settings (Lab)
                        <IconButton aria-label="close" onClick={handleCloseAdvancedSettings} sx={{ position: 'absolute', right: 8, top: 8, color: (theme: Theme) => theme.palette.grey[500] }}>
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent dividers>
                        <Stack spacing={2} sx={{ pt: 1.5, px: { xs: 0, sm: 1 } }}>
                            {[{
                                label: "Score Threshold",
                                tooltip: "Controls relevance vs. quantity (0=any, 1=perfect match).",
                                control: ( <Slider value={scoreThreshold} onChange={handleScoreThresholdChange} step={0.1} min={0.0} max={1.0} valueLabelDisplay="auto" aria-label="Score Threshold" /> )
                            }, {
                                label: "# Questions Retained",
                                tooltip: "How many past Q&A pairs to remember for context.",
                                control: ( <Slider value={historyRetention} onChange={handleHistoryRetentionChange} step={1} min={0} max={10} valueLabelDisplay="auto" aria-label="# Questions Retained" /> )
                            }].map((item, index) => (
                                <Stack key={index} direction={{ xs: "column", sm: "row" }} justifyContent={{ sm: "space-between" }} alignItems={{ sm: "center" }} spacing={{ xs: 1, sm: 2 }} sx={{ py: 1 }}>
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <Typography sx={{ whiteSpace: { sm: "nowrap" }, fontSize: { xs: '0.9rem', sm: '1rem' } }}>{item.label}</Typography>
                                        <Tooltip title={tooltipText(item.tooltip)} placement="right" arrow>
                                            <InfoIcon color="action" sx={{ cursor: "pointer", fontSize: "1rem" }} />
                                        </Tooltip>
                                    </Stack>
                                    <Box sx={{ width: { sm: "55%" } }}>{item.control}</Box>
                                </Stack>
                            ))}
                            <Divider sx={{ my: 1 }} />
                            <Stack direction={{ xs: "column", sm: "row" }} justifyContent={{ sm: "space-between" }} alignItems={{ sm: "center" }} spacing={{ xs: 1, sm: 2 }} sx={{ py: 1 }}>
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <Typography sx={{ whiteSpace: { sm: "nowrap" }, fontSize: { xs: '0.9rem', sm: '1rem' } }}>Show Metadata?</Typography>
                                    <Tooltip title={tooltipText("Yes: Show source text chunk details used for the answer.")} placement="right" arrow>
                                        <InfoIcon color="action" sx={{ cursor: "pointer", fontSize: "1rem" }} />
                                    </Tooltip>
                                </Stack>
                                <Box sx={{ width: { sm: "55%" }, display: 'flex', justifyContent: { sm: 'flex-end' } }}>
                                    <RadioGroup row value={listTextChunksFlag ? "1" : "0"} onChange={handleListTextChunksFlagChange} sx={{ flexWrap: 'nowrap' }}>
                                        <FormControlLabel value="1" control={<Radio size="small" />} label="Yes" />
                                        <FormControlLabel value="0" control={<Radio size="small" />} label="No" />
                                    </RadioGroup>
                                </Box>
                            </Stack>
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: "space-between", p: 2 }}>
                        <Button onClick={resetLabSettings} variant="outlined" color="secondary">Reset to Defaults</Button>
                        <Button onClick={handleCloseAdvancedSettings} variant="contained" color="primary">Done</Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={openFirstDialog} onClose={handleCloseFirstDialog} maxWidth="sm" fullWidth>
                    <DialogTitle>Diagram</DialogTitle>
                    <DialogContent><img src="/llm_diagram.png" alt="Diagram" style={{ maxWidth: '100%', height: 'auto', objectFit: 'contain' }} /></DialogContent>
                    <DialogActions><Button onClick={handleCloseFirstDialog}>Close</Button></DialogActions>
                </Dialog>

                <Dialog open={openSecondDialog} onClose={handleCloseSecondDialog} maxWidth="sm" fullWidth>
                    <DialogTitle>Architecture</DialogTitle>
                    <DialogContent><img src="/aws_chat_architecture.png" alt="Architecture" style={{ maxWidth: '100%', height: 'auto', objectFit: 'contain' }} /></DialogContent>
                    <DialogActions><Button onClick={handleCloseSecondDialog}>Close</Button></DialogActions>
                </Dialog>

                <Dialog open={openPdfDialog} onClose={handleClosePdfDialog} maxWidth="lg" fullWidth PaperProps={{ style: { height: '90vh' }}}>
                    <DialogTitle>Examples<IconButton aria-label="close" onClick={handleClosePdfDialog} sx={{ position: 'absolute', right: 8, top: 8, color: (theme: Theme) => theme.palette.grey[500] }}><CloseIcon /></IconButton></DialogTitle>
                    <DialogContent dividers sx={{ height: 'calc(100% - 64px - 52px)' }}> <object data="/examples.pdf" type="application/pdf" width="100%" height="100%"> <p>PDF viewer not available. <a href="/examples.pdf">Download PDF</a>.</p> </object> </DialogContent>
                    <DialogActions><Button onClick={handleClosePdfDialog}>Close</Button></DialogActions>
                </Dialog>
                {/* --- Dialogs END --- */}
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', width: '100%', height: '100%' }}>
            {isSmallScreen && (
                <IconButton
                    color="inherit"
                    aria-label="open drawer"
                    edge="start"
                    onClick={handleDrawerToggle}
                    sx={{
                        position: 'fixed',
                        top: `calc(${appBarHeight}px + ${theme.spacing(1)})`,
                        left: theme.spacing(2),
                        zIndex: theme.zIndex.drawer + 2,
                        background: 'rgba(128,128,128,0.5)',
                        color: 'white',
                        '&:hover': { background: 'rgba(128,128,128,0.7)' }
                    }}
                >
                    <MenuIcon />
                </IconButton>
            )}
            <Drawer
                variant={isSmallScreen ? "temporary" : "permanent"}
                anchor="left"
                open={isSmallScreen ? mobileOpen : true}
                onClose={isSmallScreen ? handleDrawerToggle : undefined}
                ModalProps={{ keepMounted: true }}
                sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    [`& .MuiDrawer-paper`]: {
                        width: drawerWidth,
                        boxSizing: 'border-box',
                        ...(!isSmallScreen && {
                            position: 'relative',
                            height: '100%',
                        }),
                        ...(isSmallScreen && {
                            marginTop: `${appBarHeight}px`,
                            height: `calc(100vh - ${appBarHeight}px)`,
                        })
                    },
                }}
            >
                {sidePanel}
            </Drawer>
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    overflow: 'hidden'
                }}
            >
                {(() => {
                    const currentConfig = subjectData[currentSubject];
                    const hasFilter = currentConfig && currentSubject !== GLUEUP_SUBJECT_KEY && currentConfig.contexts.length > 0;
                    const showAllOption = currentSubject !== "C40" && currentSubject !== "IPCC";
                    const filterOpts = hasFilter
                        ? (showAllOption ? ["All", ...currentConfig.contexts] : currentConfig.contexts)
                        : undefined;
                    const placeholder = currentConfig?.contextPlaceholder || currentConfig?.contextLabel || 'option';
                    return (
                        <ChatPrompt
                            scoreThreshold={scoreThreshold}
                            historyRetention={historyRetention}
                            listTextChunksFlag={listTextChunksFlag}
                            currentSubject={currentSubject}
                            subjectDisplayName={currentConfig?.name}
                            selectedDocument={selectedContexts[currentSubject]}
                            question={question}
                            autoSendTrigger={autoSendTrigger}
                            isAnyDialogOpen={isAnyDialogOpen}
                            filterLabel={hasFilter ? `Filter by ${placeholder.toLowerCase()}` : undefined}
                            filterPlaceholder={hasFilter ? placeholder : undefined}
                            filterOptions={filterOpts}
                            filterValue={hasFilter ? (selectedContexts[currentSubject] || null) : undefined}
                            onFilterChange={(v: string | null) => setSelectedContexts((prev: Record<string, string>) => ({ ...prev, [currentSubject]: v || "" }))}
                        />
                    );
                })()}
            </Box>
        </Box>
    );
}

export default ChatPage;